<template>
  <div ref="chartRef" :style="{ width: width, height: height }"></div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import * as echarts from 'echarts';

const DEFAULT_COLORS = [
  '#4facfe', '#667eea', '#06c755', '#feca57', '#e67e22',
  '#9b59b6', '#f5576c', '#1abc9c', '#3498db', '#e74c3c',
  '#2ecc71', '#f39c12', '#8e44ad', '#16a085', '#c0392b'
];

export default {
  name: 'PieChart',
  props: {
    title: {
      type: String,
      default: '分布圖'
    },
    data: {
      type: Array,
      required: true,
      default: () => []
    },
    nameKey: {
      type: String,
      default: 'name'
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '300px'
    },
    colors: {
      type: Array,
      default: () => DEFAULT_COLORS
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    radius: {
      type: Array,
      default: () => ['40%', '70%']
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    /** 切片數超過此值時改只靠圖例／tooltip，不畫外標 */
    labelMaxItems: {
      type: Number,
      default: 6
    },
    /** 外標最小占比（%）低於此不顯示 */
    labelMinPercent: {
      type: Number,
      default: 5
    }
  },
  setup(props) {
    const chartRef = ref(null);
    let chartInstance = null;

    const buildSeriesData = () => (props.data || []).map(item => ({
      name: item[props.nameKey] || '未命名',
      value: Number(item[props.valueKey]) || 0
    }));

    const getOption = () => {
      const seriesData = buildSeriesData();
      const itemCount = seriesData.length;
      const manyItems = itemCount > props.labelMaxItems;
      // 等級多時關掉外標，避免擠在一起蓋住圓餅；細節靠圖例與 tooltip
      const enableOutsideLabel = props.showLabel && !manyItems;

      return {
        title: props.showTitle ? {
          text: props.title,
          left: 'center',
          top: 0,
          textStyle: {
            fontSize: 16,
            fontWeight: 500,
            color: '#333'
          }
        } : { show: false },
        tooltip: {
          trigger: 'item',
          formatter: '{a}<br/>{b}: {c}（{d}%）'
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 4,
          top: 'middle',
          height: '72%',
          itemGap: 8,
          itemWidth: 12,
          itemHeight: 12,
          pageIconSize: 12,
          pageButtonItemGap: 4,
          pageButtonGap: 6,
          textStyle: {
            fontSize: 12,
            overflow: 'truncate',
            width: 88
          },
          formatter: (name) => {
            if (!name) return '';
            return name.length > 10 ? `${name.slice(0, 10)}…` : name;
          },
          tooltip: {
            show: true
          },
          data: seriesData.map(item => item.name)
        },
        color: props.colors?.length ? props.colors : DEFAULT_COLORS,
        series: [
          {
            name: props.title || '分布',
            type: 'pie',
            radius: props.radius,
            // 圖例在右側，圓餅偏左避免被蓋住
            center: ['38%', '54%'],
            avoidLabelOverlap: true,
            minShowLabelAngle: 8,
            label: {
              show: enableOutsideLabel,
              position: 'outside',
              formatter: (params) => {
                if (params.percent < props.labelMinPercent) return '';
                return `${params.percent}%`;
              },
              fontSize: 12
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 13,
                fontWeight: 'bold',
                formatter: '{b}\n{d}%'
              },
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            labelLine: {
              show: enableOutsideLabel,
              length: 8,
              length2: 6
            },
            data: seriesData
          }
        ]
      };
    };

    const initChart = () => {
      if (!chartRef.value) return;
      chartInstance = echarts.init(chartRef.value);
      chartInstance.setOption(getOption());
    };

    const updateChart = () => {
      if (!chartInstance) return;
      chartInstance.setOption(getOption(), true);
    };

    const resizeChart = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };

    onMounted(() => {
      initChart();
      window.addEventListener('resize', resizeChart);
    });

    watch(() => [props.data, props.showLabel, props.radius, props.title], () => {
      updateChart();
    }, { deep: true });

    onBeforeUnmount(() => {
      if (chartInstance) {
        chartInstance.dispose();
        chartInstance = null;
      }
      window.removeEventListener('resize', resizeChart);
    });

    return {
      chartRef
    };
  }
};
</script>

<style scoped>
</style>
